import { UUID } from "crypto";

import {
  AccountUser,
  CompanyData,
  CreditUseSelected,
  LoanApplication,
  PersonalData,
  ShareHolderData,
  AddressDataSession,
  DigitalPresence,
} from "@interfaces/store";
import { createSlice } from "@reduxjs/toolkit";
import type { PayloadAction } from "@reduxjs/toolkit";

export interface RegisterState {
  application: LoanApplication;
  account_user: AccountUser;
  personal_data: PersonalData;
  address_data: AddressDataSession;
  company: CompanyData;
  credit_use: CreditUseSelected;
  digital_presence: DigitalPresence;
  shareholder: ShareHolderData;
  loan_status: string;
  routes_group: "TOFU" | "BOFU" | "WAITING_ROOM" | "BOFU_COMPLETED" | "BOFU_REJECTED" | "";
  config_name: string;
  buroNip: {
    code: string;
    isValid: boolean;
    termsAccepted: boolean;
    last_code_request: string | null;
  };
  is_disabled_guarantee_type: boolean;
  rfc_approved_company?: string;
}

const initialState: RegisterState = {
  application: {
    id: "00000000-0000-0000-0000-000000000000",
    type: "",
    loan_amount: 0,
    loan_term: 0,
    amount_monthly: 0,
    tax_system_id: undefined,
    product_id: undefined,
    id_application: 0,
    approved_amount: 0,
    approved_term: 0,
    interest_rate: 0,
    application_condition_id: "",
    client_details_id: "00000000-0000-0000-0000-000000000000",
    completed: false,
    company_application_code: "",
    is_correct_ciec: false,
    source_code: "",
    ocular_visit: false,
    tax_system_type: "PM",
    identity_validation_completed: null,
    decision_validated: null,
    signature_type: "",
    financed_services_id: null,
    guarantee_type_id: null,
    loan_application_information_status: null,
    references_status: null,
    bank_information_status: null,
    guarantee_types: [],
  },
  account_user: {
    id: 0,
    email: "",
    password: "",
    phone: "",
    code_phone: "",
    phone_full: "",
  },
  personal_data: {
    id: "00000000-0000-0000-0000-000000000000",
    name: "",
    first_lastname: "",
    second_lastname: "",
    country_code: "",
    phone: "",
    person_type_id: "",
    rol_detail: "",
    cell_phone_verified: false,
    is_previous_client: null,
  },
  address_data: {
    id: "",
    street: "",
    no_ext: "",
    no_int: "",
    state_id: 0,
    city_id: 0,
    locality_id: 0,
    colony_id: 0,
    postal_code: "",
    state_name: "",
    city_name: "",
    locality_name: "",
    other_neighborhood: "",
  },
  company: {
    id: "",
    rfc: "",
    name: "",
    legal_name: "",
    role: "",
    ciec_pass: "",
    authorize_sat: false,
    is_pfae: false,
    pfae_name: "",
    pfae_last_name: "",
    pfae_last_name_2: "",
    is_correct_ciec: false,
    check_sat_bureau: false,
    country_code_legal_representative: "",
    phone_legal_representative: "",
    email_legal_representative: "",
  },
  credit_use: {
    selected: [],
    reason: "",
  },
  digital_presence: {
    id: undefined,
    has_web_page: false,
    web_page: "",
    has_social_media: false,
    linkedin: "",
    facebook: "",
    instagram: "",
    twitter: "",
  },
  shareholder: {
    id: "",
    name: "",
    first_lastname: "",
    second_lastname: "",
    date_birth: "",
    birth_state: "",
    sex: "",
    email: "",
    rfc: "",
    rfc_homoclave: "",
    curp: "",
    addressId: "",
    person_type: {
      id: "0-0-0-0-0",
      code: "",
    },
    bureau_inquiry: {
      prestamo_hipotecario: false,
      credito_auto: false,
      tarjeta_credito: false,
      tarjeta: "",
      autorizacion_buro: false,
    },
    check_sat_bureau: false,
    code_phone_number: "",
    phone_number: "",
    address_verification_required: null,
  },
  loan_status: "",
  routes_group: "",
  config_name: "",
  buroNip: {
    code: "",
    isValid: false,
    termsAccepted: false,
    last_code_request: null,
  },
  is_disabled_guarantee_type: false,
  rfc_approved_company: "",
};

export const registerSlice = createSlice({
  name: "register",
  initialState,
  reducers: {
    setTaxSystemType: (state, action: PayloadAction<"PM" | "PFAE">) => {
      state.application.tax_system_type = action.payload;
    },
    setConditionLoan: (
      state,
      action: PayloadAction<{
        loanTerm: number;
        loanAmount: number;
        amountMonthly: number;
        advance_percentage?: number | null;
        residual_percentage?: number | null;
      }>,
    ) => {
      state.application.loan_term = action.payload.loanTerm;
      state.application.loan_amount = action.payload.loanAmount;
      state.application.amount_monthly = action.payload.amountMonthly;
      state.application.advance_percentage = action.payload.advance_percentage ?? null;
      state.application.residual_percentage = action.payload.residual_percentage ?? null;
    },
    setNewConditionLoan: (
      state,
      action: PayloadAction<{
        loanTerm: number;
        loanAmount: number;
        amountMonthly: number;
        approved_amount: number;
        approved_term: number;
        interest_rate: number;
      }>,
    ) => {
      state.application.loan_term = action.payload.loanTerm;
      state.application.loan_amount = action.payload.loanAmount;
      state.application.amount_monthly = action.payload.amountMonthly;
      state.application.approved_amount = action.payload.approved_amount;
      state.application.approved_term = action.payload.approved_term;
      state.application.interest_rate = action.payload.interest_rate;
    },
    setLoanId: (state, action: PayloadAction<string>) => {
      state.application.id = action.payload;
    },
    setClientDetailsId: (state, action: PayloadAction<string>) => {
      state.application.client_details_id = action.payload;
    },
    setTaxProductId: (
      state,
      action: PayloadAction<{ tax_system_id: UUID; product_id: UUID; source_code: string; type: string }>,
    ) => {
      state.application.tax_system_id = action.payload.tax_system_id;
      state.application.product_id = action.payload.product_id;
      state.application.source_code = action.payload.source_code;
      state.application.type = action.payload.type;
    },
    setLoanApplication: (state, action: PayloadAction<LoanApplication>) => {
      const newPayload = { ...action.payload };

      if (newPayload.tax_systems) {
        newPayload.tax_system_type = newPayload.tax_systems.tax_system_type;
      }

      if (newPayload.loan_application_conditions && newPayload.loan_application_conditions.length) {
        const first_condition = newPayload.loan_application_conditions[0];
        newPayload.application_condition_id = first_condition.id;
        newPayload.loan_amount = first_condition.loan_amount;
        newPayload.loan_term = first_condition.requested_term;
        newPayload.approved_amount = first_condition.approved_amount;
        newPayload.approved_term = first_condition.approved_term;
        newPayload.interest_rate = first_condition.interest_rate;
        newPayload.amount_monthly = first_condition.monthly_payment;
      }

      state.application = {
        ...state.application,
        ...newPayload,
      };
    },
    setId_application: (state, action: PayloadAction<number>) => {
      state.application.id_application = action.payload;
    },
    set_Id_applicationCondition: (state, action: PayloadAction<string>) => {
      state.application.application_condition_id = action.payload;
    },
    setAccountUser: (state, action: PayloadAction<RegisterState["account_user"]>) => {
      state.account_user.id = action.payload.id;
      state.account_user.email = action.payload.email;
      state.account_user.password = action.payload.password;
      const phoneFull = action.payload.phone;

      // Código de país de 2 dígitos y número de 10 dígitos
      const match = phoneFull?.match(/^\+(\d{2})(\d{10})$/);

      if (match) {
        const [, codePhone, phone] = match;
        state.account_user.phone_full = phoneFull;
        state.account_user.code_phone = `+${codePhone}`; // "52"
        state.account_user.phone = phone; // "9903202115"
      }
    },
    setAccountUserId: (state, action: PayloadAction<RegisterState["account_user"]["id"]>) => {
      state.account_user.id = action.payload;
    },
    setAccountUserPhone: (
      state,
      action: PayloadAction<{
        phone_full: RegisterState["account_user"]["phone_full"];
        code_phone: string;
        phone: string;
      }>,
    ) => {
      state.account_user.phone_full = action.payload.phone_full;
      state.account_user.code_phone = action.payload.code_phone;
      state.account_user.phone = action.payload.phone;
    },
    setPersonalData: (state, action: PayloadAction<RegisterState["personal_data"]>) => {
      state.personal_data.id = action.payload.id;
      state.personal_data.name = action.payload.name;
      state.personal_data.first_lastname = action.payload.first_lastname;
      state.personal_data.second_lastname = action.payload.second_lastname;
      state.personal_data.country_code = action.payload.country_code;
      state.personal_data.phone = action.payload.phone;
      state.personal_data.person_type_id = action.payload.person_type_id;
      state.personal_data.rol_detail = action.payload.rol_detail;
      state.personal_data.cell_phone_verified = action.payload.cell_phone_verified;
    },
    setAddressData: (state, action: PayloadAction<RegisterState["address_data"]>) => {
      state.address_data.id = action.payload.id;
      state.address_data.street = action.payload.street;
      state.address_data.no_ext = action.payload.no_ext;
      state.address_data.no_int = action.payload.no_int;
      state.address_data.state_id = action.payload.state_id;
      state.address_data.city_id = action.payload.city_id;
      state.address_data.postal_code = action.payload.postal_code;
      state.address_data.state_name = action.payload.state_name;
      state.address_data.city_name = action.payload.city_name;
      state.address_data.other_neighborhood = action.payload.other_neighborhood;
      state.address_data.locality_id = action.payload.locality_id;
      state.address_data.locality_name = action.payload.locality_name;
      state.address_data.colony_id = action.payload.colony_id;
    },
    setCompanyData: (state, action: PayloadAction<RegisterState["company"]>) => {
      state.company.id = action.payload.id;
      state.company.rfc = action.payload.rfc;
      state.company.name = action.payload.name;
      state.company.legal_name = action.payload.legal_name;
      state.company.is_pfae = action.payload.is_pfae;
      state.company.pfae_name = action.payload.pfae_name;
      state.company.pfae_last_name = action.payload.pfae_last_name;
      state.company.pfae_last_name_2 = action.payload.pfae_last_name_2;
      state.company.is_correct_ciec = action.payload.is_correct_ciec;
      state.company.country_code_legal_representative = action.payload.country_code_legal_representative;
      state.company.phone_legal_representative = action.payload.phone_legal_representative;
      state.company.email_legal_representative = action.payload.email_legal_representative;
    },
    setShareHolderData: (state, action: PayloadAction<RegisterState["shareholder"]>) => {
      state.shareholder.id = action.payload.id;
      state.shareholder.name = action.payload.name;
      state.shareholder.first_lastname = action.payload.first_lastname;
      state.shareholder.second_lastname = action.payload.second_lastname;
      state.shareholder.date_birth = action.payload.date_birth;
      state.shareholder.birth_state = action.payload.birth_state;
      state.shareholder.sex = action.payload.sex;
      state.shareholder.email = action.payload.email;
      state.shareholder.rfc = action.payload.rfc;
      state.shareholder.email = action.payload.email;
      state.shareholder.person_type = action.payload.person_type;
      state.shareholder.code_phone_number = action.payload.code_phone_number;
      state.shareholder.phone_number = action.payload.phone_number;
    },
    setShareHolderRfcCurp: (state, action: PayloadAction<{ rfc: string; rfc_homoclave: string; curp: string }>) => {
      state.shareholder.rfc = action.payload.rfc;
      state.shareholder.rfc_homoclave = action.payload.rfc_homoclave;
      state.shareholder.curp = action.payload.curp;
    },
    setShareholderUpdatePhone: (state, action: PayloadAction<{ code_phone_number: string; phone_number: string }>) => {
      state.shareholder.code_phone_number = action.payload.code_phone_number;
      state.shareholder.phone_number = action.payload.phone_number;
    },
    setLoanStatus: (state, action: PayloadAction<string>) => {
      state.loan_status = action.payload;

      // if (STATUS_TOFU.includes(action.payload)) {
      //   state.routes_group = "TOFU";
      // } else {
      //   state.routes_group = "BOFU";
      // }
    },
    setCheckSatBureau: (state, action: PayloadAction<{ check_sat_bureau: boolean }>) => {
      state.shareholder.check_sat_bureau = action.payload.check_sat_bureau;
    },
    setCheckSatBureauPfae: (state, action: PayloadAction<{ check_sat_bureau: boolean }>) => {
      state.company.check_sat_bureau = action.payload.check_sat_bureau;
    },
    setConfigName: (state, action: PayloadAction<string>) => {
      state.config_name = action.payload;
    },
    setBuroNip: (state, action: PayloadAction<{ code: string; isValid: boolean; termsAccepted: boolean }>) => {
      state.buroNip.code = action.payload.code;
      state.buroNip.isValid = action.payload.isValid;
      state.buroNip.termsAccepted = action.payload.termsAccepted;
    },
    setBuroNipLastCodeRequest: (state, action: PayloadAction<{ last_code_request: string | null }>) => {
      state.buroNip.last_code_request = action.payload.last_code_request;
    },
    setGuaranteeTypeId: (
      state,
      action: PayloadAction<{ guarantee_type_id: string | null; description_guarantee?: string }>,
    ) => {
      state.application.guarantee_type_id = action.payload.guarantee_type_id;
      state.application.description_guarantee = action.payload.description_guarantee;
    },
    setGuaranteeTypes: (state, action: PayloadAction<{ guarantee_types: string[] }>) => {
      state.application.guarantee_types = action.payload.guarantee_types;
    },
    setFinancedServicesId: (state, action: PayloadAction<{ financed_services_id: string | null }>) => {
      state.application.financed_services_id = action.payload.financed_services_id;
    },
    setIsPreviousClient: (state, action: PayloadAction<{ is_previous_client: boolean | null }>) => {
      state.personal_data.is_previous_client = action.payload.is_previous_client;
    },
    setIsDisabledGuaranteeTypeSelect: (state, action: PayloadAction<{ disabled: boolean }>) => {
      state.is_disabled_guarantee_type = action.payload.disabled;
    },
    setRfcApprovedCompany: (state, action: PayloadAction<{ rfc_approved_company: string }>) => {
      state.rfc_approved_company = action.payload.rfc_approved_company;
    },
  },
});

export const {
  setTaxSystemType,
  setConditionLoan,
  setLoanId,
  setClientDetailsId,
  setId_application,
  setLoanApplication,
  setAccountUser,
  setAccountUserId,
  setPersonalData,
  setCompanyData,
  setShareHolderData,
  setTaxProductId,
  setShareHolderRfcCurp,
  set_Id_applicationCondition,
  // updateApplicationCompleted,
  setNewConditionLoan,
  setLoanStatus,
  setAddressData,
  setCheckSatBureau,
  setConfigName,
  setBuroNip,
  setBuroNipLastCodeRequest,
  setCheckSatBureauPfae,
  setGuaranteeTypeId,
  setGuaranteeTypes,
  setFinancedServicesId,
  setIsPreviousClient,
  setIsDisabledGuaranteeTypeSelect,
  setRfcApprovedCompany,
  setAccountUserPhone,
  setShareholderUpdatePhone,
} = registerSlice.actions;
export default registerSlice.reducer;
